// @flow
import type {
  RequestProductsPayload,
  FlattenedProductResponse,
  Variants,
  StateProducts,
  Favorites,
  FavoritesResponse,
  FavoritesUpdateRequest,
} from '../../sharedTypes';

export const PRODUCTS = {
  REQUEST_PRODUCTS: 'products/request_products',
  FAILED_PRODUCTS_REQUEST: 'products/failed_products/request',
  RECEIVE_PRODUCTS: 'products/receive_products',
  RECEIVE_MORE_PRODUCTS: 'products/receive_more_products',
  RECEIVE_SPONSORED_PRODUCTS: 'products/receive_sponsored_products',
  RECEIVE_ALL_PRODUCTS: 'products/receive_all_products',
  RECEIVE_CART_PRODUCTS: 'products/receive_cart_products',
  REQUEST_FAVORITES: 'products/request_favorites',
  SET_FAVORITES: 'products/set_favorites',
  REQUEST_TOGGLE_FAVORITE: 'products/request_toggle_favorite',
  RECEIVE_TOGGLED_FAVORITE: 'products/receive_toggled_favorite',
  TOGGLE_SOLDOUT: 'products/toggle_soldout',
  DISPLAY_SPONSORED_POPUP_WARNING: 'products/display_sponsored_popup_warning',
  DISPLAY_REMOVE_GWP_DISQUALIFY_POPUP: 'products/display_remove_gwp_disqualify_popup',
  DISPLAY_REMOVE_GWP_DOWNGRADE_POPUP: 'products/display_remove_gwp_downgrade_popup',
  DISPLAY_REMOVE_GWP_OUT_OF_STOCK_POPUP: 'products/display_remove_gwp_out_of_stock_popup',
  DISPLAY_REMOVE_GWP_WARNING_POPUP: 'products/display_remove_gwp_warning_popup',
  DISPLAY_SPONSORED_ACCORDION_WARNING: 'products/display_sponsored_accordion_warning',
  HIDE_SPONSORED_WARNING: 'products/hide_sponsored_warning',
  SET_OVERRIDE_DEFAULT_VARIANT_ID: 'products/set_override_default_variant_id',
  REQUEST_BEST_SELLING_PRODUCTS: 'products/request_best_selling_products',
  RECEIVE_BEST_SELLING_PRODUCTS: 'products/receive_best_selling_products',
  RECEIVE_BEST_SELLING_PRODUCTS_ERROR: 'products/receive_best_selling_products_error',
  REQUEST_PRODUCTS_BY_ID: 'products/request_products_by_id',
  REQUEST_SEARCH_PRODUCTS: 'products/request_search_products',
  RECEIVE_SEARCH_PRODUCTS: 'product/receive_search_products',
};

export type RequestFavoritesAction = {|
  type: typeof PRODUCTS.REQUEST_FAVORITES,
|};

export type SetFavoritesAction = {|
  type: typeof PRODUCTS.SET_FAVORITES,
  favorites: FavoritesResponse,
|};

export type RequestToggleFavoritesAction = {|
  type: typeof PRODUCTS.REQUEST_TOGGLE_FAVORITE,
  productId: string,
  variantId: string,
  buttonLocation: string,
  isLike: boolean,
  selectedCategoryId: string | null,
|};

export type RequestProductsAction = {|
  type: typeof PRODUCTS.REQUEST_PRODUCTS,
  payload: RequestProductsPayload,
|};

export type FailedProductsRequestAction = {|
  type: typeof PRODUCTS.FAILED_PRODUCTS_REQUEST,
|};

export type ReceiveProductsAction = {|
  type: typeof PRODUCTS.RECEIVE_PRODUCTS,
  payload: FlattenedProductResponse,
|};

export type ReceiveMoreProductsAction = {|
  type: typeof PRODUCTS.RECEIVE_MORE_PRODUCTS,
  payload: FlattenedProductResponse,
|};

export type ReceiveSponsoredProductsAction = {|
  type: typeof PRODUCTS.RECEIVE_SPONSORED_PRODUCTS,
  payload: FlattenedProductResponse,
|};

export type ReceiveCartProductsAction = {|
  type: typeof PRODUCTS.RECEIVE_CART_PRODUCTS,
  payload: {| variants: Variants, products: StateProducts |},
|};

export type DisplaySponsoredAccordionWarningAction = {|
  type: typeof PRODUCTS.DISPLAY_SPONSORED_ACCORDION_WARNING,
  variantId: string,
|};

export type DisplaySponsoredPopupWarningAction = {|
  type: typeof PRODUCTS.DISPLAY_SPONSORED_POPUP_WARNING,
  variantId: string,
|};

export type HideSponsoredWarningAction = {|
  type: typeof PRODUCTS.HIDE_SPONSORED_WARNING,
|};

export type SetOverrideDefaultVariantIdAction = {|
  type: typeof PRODUCTS.SET_OVERRIDE_DEFAULT_VARIANT_ID,
  variantId: string,
|};

export function requestFavorites() {
  return {
    type: PRODUCTS.REQUEST_FAVORITES,
  };
}

export function setFavorites(favorites: Favorites) {
  return {
    type: PRODUCTS.SET_FAVORITES,
    favorites,
  };
}

export function requestToggleFavorite(
  productId: string,
  variantId: string,
  buttonLocation: string,
  isLike: string,
  selectedCategoryId: string | null,
) {
  return {
    type: PRODUCTS.REQUEST_TOGGLE_FAVORITE,
    productId,
    variantId,
    buttonLocation,
    isLike,
    selectedCategoryId,
  };
}

export function receiveToggledFavorite(payload: FavoritesUpdateRequest) {
  return {
    type: PRODUCTS.RECEIVE_TOGGLED_FAVORITE,
    payload,
  };
}

export function requestProducts(payload: RequestProductsPayload) {
  return {
    type: PRODUCTS.REQUEST_PRODUCTS,
    payload,
  };
}

export function failedProductsRequest() {
  return {
    type: PRODUCTS.FAILED_PRODUCTS_REQUEST,
  };
}

export function receiveProducts(payload: FlattenedProductResponse) {
  return {
    type: PRODUCTS.RECEIVE_PRODUCTS,
    payload,
  };
}

export function receiveMoreProducts(payload: FlattenedProductResponse) {
  return {
    type: PRODUCTS.RECEIVE_MORE_PRODUCTS,
    payload,
  };
}

export function receiveSponsoredProducts(payload: FlattenedProductResponse) {
  return {
    type: PRODUCTS.RECEIVE_SPONSORED_PRODUCTS,
    payload,
  };
}

export function receiveCartProducts(payload: {| variants: Variants, products: StateProducts |}) {
  return {
    type: PRODUCTS.RECEIVE_CART_PRODUCTS,
    payload,
  };
}

export function toggleSoldOut(variantId: string, soldOut: boolean) {
  return {
    type: PRODUCTS.TOGGLE_SOLDOUT,
    variantId,
    soldOut,
  };
}

export function displaySponsoredAccordionWarning(variantId: string) {
  return {
    type: PRODUCTS.DISPLAY_SPONSORED_ACCORDION_WARNING,
    variantId,
  };
}

export function displaySponsoredPopupWarning(variantId: string) {
  return {
    type: PRODUCTS.DISPLAY_SPONSORED_POPUP_WARNING,
    variantId,
  };
}

export function displayRemoveGWPDisqualifyPopup(variantId: string) {
  return {
    type: PRODUCTS.DISPLAY_REMOVE_GWP_DISQUALIFY_POPUP,
    variantId,
  };
}

export function displayRemoveGWPDowngradePopup(variantId: string) {
  return {
    type: PRODUCTS.DISPLAY_REMOVE_GWP_DOWNGRADE_POPUP,
    variantId,
  };
}

export function displayRemoveGWPOutOfStockPopup(variantId: string) {
  return {
    type: PRODUCTS.DISPLAY_REMOVE_GWP_OUT_OF_STOCK_POPUP,
    variantId,
  };
}

export function displayRemoveGWPWarningPopup(variantId: string) {
  return {
    type: PRODUCTS.DISPLAY_REMOVE_GWP_WARNING_POPUP,
    variantId,
  };
}

export function hideSponsoredWarning() {
  return {
    type: PRODUCTS.HIDE_SPONSORED_WARNING,
  };
}

export function setOverrideDefaultVariantId(variantId: string | null) {
  return {
    type: PRODUCTS.SET_OVERRIDE_DEFAULT_VARIANT_ID,
    variantId,
  };
}

export function requestBestSellingProducts(payload: RequestProductsPayload) {
  return {
    type: PRODUCTS.REQUEST_BEST_SELLING_PRODUCTS,
    payload,
  };
}

export function receiveBestSellingProducts(payload) {
  return {
    type: PRODUCTS.RECEIVE_BEST_SELLING_PRODUCTS,
    payload,
  };
}

export function receiveBestSellingProductsError(payload) {
  return {
    type: PRODUCTS.RECEIVE_BEST_SELLING_PRODUCTS_ERROR,
    payload,
  };
}

export function requestProductsById(productIds) {
  return {
    type: PRODUCTS.REQUEST_PRODUCTS_BY_ID,
    productIds,
  };
}

export function requestSearchProducts(productIds) {
  return {
    type: PRODUCTS.REQUEST_SEARCH_PRODUCTS,
    productIds,
  };
}

export function receiveSearchProducts(payload) {
  return {
    type: PRODUCTS.RECEIVE_SEARCH_PRODUCTS,
    payload,
  };
}
